import { memo, useMemo } from 'react'
import { ArrowUp, CalendarClock, ChevronDown, Edit3, Eye, Trash2 } from 'lucide-react'
import { DropdownMenu } from '@/components/common'
import { PrepackCardView } from '@feed-widget/surfaces/PrepacksPresentation'
import { STARTER_TEMPLATES, toPublicPrepack } from './prepackCardModel'
import { usePrepackPreview } from './prepackPreviewContext'
import { preloadScheduleBuilder } from './prepackSchedulePaths'
import { artworkColorScheme, artworkVariables, buildSrcDoc } from '../../../../../shared/pkg/prepacks/templateRendering.js'
import { CARD_PICTURE_BACKGROUND, hasFullCardPicture } from '../../../../../shared/pkg/prepacks/bannerFills.js'

// Raw code-editor preview intentionally includes the template's own sample payload.
export function TemplateIframePreview({ template: templateProp, tpl, height = 220, scale, varOverrides, pack, colorMode }) {
  const template = templateProp || tpl
  const { branding } = usePrepackPreview()
  const mode = colorMode || (branding.darkMode ? 'dark' : 'light')
  // The frame wears the scheme its artwork declares, so its canvas stays transparent.
  const style = { width: '100%', height, border: 0, display: 'block', background: 'transparent', colorScheme: artworkColorScheme(template, mode) }
  // Artwork that runs its picture under the footer paints none itself (#1038): the frame stands in for the card.
  if (hasFullCardPicture(template.cssCode)) {
    for (const [key, value] of artworkVariables(template, mode, varOverrides)) style[key] = value
    style.background = CARD_PICTURE_BACKGROUND
  }
  return (
    <iframe
      srcDoc={buildSrcDoc(template, varOverrides, scale, pack, { colorMode: mode })}
      sandbox="allow-same-origin"
      scrolling="no"
      style={style}
      title={template.name}
    />
  )
}

// The Edit control's look under a pack card and in the Library drawer: the
// classes every half shares, then the corners and width of the button alone,
// of the split's main half, and of its chevron half.
const EDIT_LOOKS = {
  card: {
    group: 'flex',
    button: 'flex items-center gap-1 bg-[var(--prepacks-accent)] py-1.5 text-xs text-white',
    alone: 'rounded px-3',
    main: 'rounded-l px-3',
    toggle: 'rounded-r px-2',
  },
  drawer: {
    group: 'flex flex-1',
    button: 'flex items-center justify-center gap-1.5 bg-[var(--prepacks-accent)] py-2 text-xs font-medium text-[var(--wf-color-on-action)] transition-colors hover:bg-[var(--prepacks-accent-hover)]',
    alone: 'flex-1 rounded-lg',
    // Beside the chevron the label would break over two lines; unbroken, a
    // narrow row moves the next button down instead.
    main: 'flex-1 whitespace-nowrap rounded-l-lg px-3',
    toggle: 'rounded-r-lg px-2.5',
  },
}

/**
 * A pack's Edit control (#863). A pack a schedule generated (`onEditSchedule`)
 * gets a split button: the main half edits the pack, the chevron offers the
 * pack or its source schedule, named in the menu's footer (`scheduleSource`,
 * `{ id, name, missing }`; a deleted schedule's item is disabled). A pack built
 * by hand keeps one button.
 */
export function PackEditActions({ pack, label = 'Edit', variant = 'card', onEdit, onEditSchedule, scheduleSource }) {
  const look = EDIT_LOOKS[variant] || EDIT_LOOKS.card
  const icon = <Edit3 className="h-3.5 w-3.5" aria-hidden="true" />
  if (!onEditSchedule) {
    return <button type="button" onClick={onEdit} className={`${look.button} ${look.alone}`}>{icon}{label}</button>
  }
  const missing = Boolean(scheduleSource?.missing)
  const optionsLabel = `Edit options for ${pack.title}`
  return (
    <div role="group" aria-label={`Edit ${pack.title}`} className={look.group}>
      <button type="button" onClick={onEdit} className={`${look.button} ${look.main}`}>{icon}{label}</button>
      <DropdownMenu
        variant="rich"
        align="start"
        triggerLabel={optionsLabel}
        trigger={(
          <button
            type="button"
            aria-label={optionsLabel}
            onMouseEnter={preloadScheduleBuilder}
            onFocus={preloadScheduleBuilder}
            className={`${look.button} ${look.toggle} border-l border-[var(--wf-color-on-action)]/30`}
          >
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
        items={[
          { key: 'pack', icon: Edit3, label: 'Edit this prepack', description: 'Change only this pack', onSelect: onEdit },
          {
            key: 'schedule',
            icon: CalendarClock,
            label: 'Edit source schedule',
            description: missing ? 'The schedule was deleted' : 'Change it for all its packs',
            disabled: missing,
            onSelect: onEditSchedule,
          },
        ]}
        footer={<><strong>Source:</strong> {missing ? 'a deleted schedule' : (scheduleSource?.name || 'Schedule')}</>}
      />
    </div>
  )
}

// Memoized: the builder re-renders on every keystroke, and a card with an
// unchanged pack has nothing to update.
export const PrepackCard = memo(function PrepackCard({
  pack: rawPack,
  showActions = true,
  compact = false,
  templates = STARTER_TEMPLATES,
  colorMode,
  onEdit,
  onEditSchedule,
  scheduleSource,
  onDelete,
  onMoveTop,
  // Show now (#1040): a waiting pool pack takes a position on the surface;
  // showDisabled while another lineup write runs.
  onShow,
  showDisabled = false,
}) {
  const pack = useMemo(() => toPublicPrepack(rawPack), [rawPack])
  const { branding, cssVars, fallbackTemplate, surface, oddsFormat, unavailable } = usePrepackPreview()
  const assignedTemplate = pack.template_id ? templates.find((item) => item.id === pack.template_id) : null
  const template = assignedTemplate || templates.find((item) => item.isFallback) || fallbackTemplate
  // A new object each render makes React re-check every branding variable.
  const style = useMemo(() => ({ ...cssVars, containerType: 'inline-size' }), [cssVars])

  return (
    <div className={`w-full min-w-0 ${compact ? '' : 'max-w-[380px]'}`}>
      <div className={`prepacks-surface--density-${surface?.layout_config?.card_density || 'comfortable'}`} style={style}>
        {unavailable ? <p className="text-sm text-[var(--prepacks-muted)]">Card preview unavailable until branding loads.</p> : <PrepackCardView
          pack={pack}
          template={template}
          branding={branding}
          colorMode={colorMode || (branding.darkMode ? 'dark' : 'light')}
          oddsFormat={oddsFormat}
          exampleStake={surface?.layout_config?.example_stake}
          ctaLabel={surface?.layout_config?.cta_label || branding.buttonText || 'Add to Betslip'}
          showOddsBadge={surface?.layout_config?.show_odds_badge !== false}
          readOnly={Boolean(surface) && surface.betting_mode !== 'wapi_kambi'}
          preview
        />}
      </div>
      {showActions && (onShow || onMoveTop || onEdit || onDelete) ? (
        <div className="mt-2 flex flex-wrap gap-2" aria-label="Prepack management">
          {onShow ? <button type="button" disabled={showDisabled} onClick={onShow} aria-label={`Show ${pack.title} on this surface`} className="flex items-center gap-1 rounded disabled:opacity-50 border border-[var(--prepacks-border)] bg-[var(--prepacks-raised)] px-3 py-1.5 text-xs text-[var(--prepacks-text)]"><Eye className="h-3.5 w-3.5" />Show now</button> : null}
          {onMoveTop ? <button type="button" onClick={onMoveTop} className="flex items-center gap-1 rounded border border-[var(--prepacks-border)] bg-[var(--prepacks-raised)] px-3 py-1.5 text-xs text-[var(--prepacks-text)]"><ArrowUp className="h-3.5 w-3.5" />Top</button> : null}
          {onEdit ? <PackEditActions pack={pack} onEdit={onEdit} onEditSchedule={onEditSchedule} scheduleSource={scheduleSource} /> : null}
          {onDelete ? <button type="button" onClick={onDelete} className="flex items-center gap-1 rounded border border-[var(--wf-color-danger-border)] bg-[var(--wf-color-danger-soft)] px-3 py-1.5 text-xs text-[var(--wf-color-danger)]"><Trash2 className="h-3.5 w-3.5" />Remove</button> : null}
        </div>
      ) : null}
    </div>
  )
})
